import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import DropZoneComponent from '../../../components/common/DropZone';
import { IMG_SCALE_SIZE } from '../../../utility/constants';
import { Checkbox } from '@paypalcorp/pp-react-checkbox';
import { Button } from '@paypalcorp/pp-react-buttons';
import {
  Context as WorldReadyContext,
  Message,
} from '@paypalcorp/worldready-react';
import { change } from 'redux-form';
import { BTN_FACTORY_V3_FORM, ERRORS } from '../../clientConstants';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { CloseIcon } from '@paypalcorp/pp-react-icons';
import { Alert } from '@paypalcorp/pp-react-alert';
import { Link } from '@paypalcorp/pp-react-links';
import { TextDisplay } from '@paypalcorp/pp-react-text-input';
import _isEmpty from 'lodash/isEmpty';
import {
  LOGO_TARGET_BUTTON,
  LOGO_TARGET_NOLOGO,
  LOGO_TARGET_PROFILE,
} from '../../reducers/reducerButtonFactoryV3';
import { trackEventWithData } from '../../utilsV3/analytics';
import { FPTI_SPEC, SCREEN } from '../../utilsV3/fptiConstants';
import { getUserType, isCsamHit } from '../../utilsV3/buttonFactoryUtils';
import { MessageFormat } from '@paypalcorp/worldready';

const DropzoneLogo = ({
  dispatch,
  uploadImage,
  updateBusinessProfileLogo,
  updateBusinessImageUrls,
  closeModal,
  logoUrl,
  setLogoImg,
  logoTarget,
  isLogoBusinessProfile,
  setIsLogoBusinessProfile,
}) => {
  const worldReady = useContext(WorldReadyContext);
  const saveButton = <Message id="buttonsV3.logoExperience.modal.saveButton" />;
  const deleteButton = (
    <Message id="buttonsV3.imageBanner.modal.deleteButton" />
  );
  const userType = getUserType();

  const [imageData, setImageData] = useState({});
  const [showSpinner, setShowSpinner] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState(true);
  const [imageUpdated, setImageUpdated] = useState(false);
  const [dropzoneError, setDropzoneError] = useState('');
  const [showSaveButton, setShowSaveButton] = useState(false);

  const handleImageDropzone = (image) => {
    setDropzoneError('');
    if (image.hasError) {
      setDropzoneError(image.error);
      trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
        error_code: 'IMAGE_DROPZONE_ERROR',
        error_desc: 'logo',
        error_detail: image.error,
        screen: SCREEN.EXPERIENCE_SETUP,
      });
    } else {
      trackEventWithData(FPTI_SPEC.UPLOAD_LOGO_MODAL_IMAGE_UPDATED);
      setImageUpdated(true);
      setImageData(image);
      setIsLogoBusinessProfile(false);
    }
  };

  useEffect(() => {
    if (logoUrl) {
      setImageData({
        src: logoUrl,
        name: logoUrl.split('/').pop(),
      });
    }
  }, []);

  const handleImageUpload = async () => {
    return new Promise((resolve, reject) => {
      const imageDataSrc = imageData.src;
      if (imageDataSrc) {
        uploadImage(imageDataSrc)
          .then((response) => {
            if (response.ok) {
              return resolve(response);
            }

            let error = {
              processingStage: 'IMAGE_UPLOAD_ERROR',
              ...response,
            };
            trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
              error_code: 'LOGO_IMAGE_UPLOAD_ERROR',
              error_desc: 'logo',
              error_detail: JSON.stringify(response),
              screen: SCREEN.EXPERIENCE_SETUP,
            });
            return reject(error);
          })
          .catch((error) => {
            trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
              error_code: 'LOGO_IMAGE_UPLOAD_ERROR',
              error_desc: 'logo',
              error_detail: JSON.stringify(error),
              screen: SCREEN.EXPERIENCE_SETUP,
            });
            return reject({ ...error, processingStage: 'IMAGE_UPLOAD_ERROR' });
          });
      }
    });
  };

  const handleBusinessProfileUpdate = async (url) => {
    return new Promise((resolve, reject) => {
      updateBusinessProfileLogo(url)
        .then((response) => {
          if (response.ok) {
            return resolve(response);
          }

          let error = {
            processingStage: 'BUSINESS_PROFILE_IMAGE_UPDATE_ERROR',
            response,
          };
          return reject(error);
        })
        .catch((error) => {
          return reject({
            ...error,
            processingStage: 'BUSINESS_PROFILE_IMAGE_UPDATE_ERROR',
          });
        });
    });
  };

  const updateLogo = (url) => {
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoUrl', url));
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoTarget', LOGO_TARGET_BUTTON));
    setLogoImg(url);
    setShowSpinner(false);
    setIsLogoBusinessProfile(false);
    closeModal();
  };

  const updateSettingsLogo = (url) => {
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoUrl', ''));
    dispatch(change(BTN_FACTORY_V3_FORM, 'businessProfileLogoUrl', url));
    updateBusinessImageUrls(url);
    dispatch(change(BTN_FACTORY_V3_FORM, 'logoTarget', LOGO_TARGET_PROFILE));
    setIsLogoBusinessProfile(true);
    setLogoImg(url);
    setShowSpinner(false);
    closeModal();
  };

  const removeImage = () => {
    setImageData({});
    setIsLogoBusinessProfile(true);
    setShowSaveButton(true);
  };

  const saveLogo = async () => {
    trackEventWithData(FPTI_SPEC.UPLOAD_LOGO_MODAL_SAVE_CL);

    let src = imageData?.src ?? '';
    if (_isEmpty(src)) {
      dispatch(change(BTN_FACTORY_V3_FORM, 'logoUrl', ''));
      dispatch(change(BTN_FACTORY_V3_FORM, 'logoTarget', LOGO_TARGET_NOLOGO));
      closeModal();
    } else if (imageUpdated) {
      try {
        setShowSpinner(true);
        let response = await handleImageUpload();
        if (response.success) {
          // BUSINESS PROFILE LOGO UPDATE
          if (checkboxValue && userType === 'BUSINESS') {
            try {
              let businessProfileResponse = await handleBusinessProfileUpdate(
                response.src
              );
              if (businessProfileResponse.success) {
                updateSettingsLogo(response.src);
              } else {
                updateLogo(response.src);
                // TODO: Manage business profile error (Content is in progress)
              }
            } catch (e) {
              updateLogo(response.src);
            }
          } else {
            updateLogo(response.src);
          }
        } else {
          setShowSpinner(false);
          setDropzoneError(
            new MessageFormat(worldReady, {
              id: 'buttonsV3.fieldErrors.dropzone.tryAgain',
            }).format()
          );
          setLogoImg('');
        }
      } catch (err) {
        if (isCsamHit(err)) {
          window.location.href = `/donate/buttons/error?name=${ERRORS.RESTRICTED_ACCOUNT}`;
        } else {
          setShowSpinner(false);
          setDropzoneError(
            new MessageFormat(worldReady, {
              id: 'buttonsV3.fieldErrors.dropzone.tryAgain',
            }).format()
          );
          setLogoImg('');
        }
      }
    } else {
      closeModal();
    }
  };

  const imageDataSrc = imageData.src;
  return (
    <div>
      {imageDataSrc && (
        <>
          <div className="logo-remove">
            <IconButton
              onClick={removeImage}
              svgIconComponent={CloseIcon}
              className="delete-img"
            >
              {deleteButton}
            </IconButton>
          </div>
        </>
      )}
      <div className={`${imageDataSrc ? 'logo-dropzone' : ''}`}>
        <DropZoneComponent
          imgURL={imageData?.src}
          onChange={handleImageDropzone}
          imgWidth={IMG_SCALE_SIZE.WIDTH_LOGO}
          showPreviewInDropzone={true}
          displayEditIcon={true}
          setDefaultHeight={logoTarget === LOGO_TARGET_PROFILE ? true : false}
          showSolidBorder={true}
          fptiSpec={{
            updated: FPTI_SPEC.CONFIGURE_BUTTON_PAGE_LOGO_UPDATED,
            change: FPTI_SPEC.CONFIGURE_BUTTON_PAGE_CHANGE_LOGO_CL,
          }}
        />
        {imageUpdated ? (
          <>
            {!isLogoBusinessProfile && userType === 'BUSINESS' && (
              <label
                style={{
                  flexDirection: 'row',
                  alignItems: 'baseline',
                  display: 'flex',
                }}
              >
                <Checkbox
                  checked={checkboxValue}
                  onChange={() => {
                    trackEventWithData(
                      FPTI_SPEC.UPLOAD_LOGO_MODAL_CHECKBOX_CL,
                      {
                        checked: !checkboxValue,
                      }
                    );
                    setCheckboxValue(!checkboxValue);
                  }}
                  className="business-checkbox"
                  name="updateBusinessProfile"
                />
                <div>
                  <TextDisplay
                    name="basic"
                    label=""
                    value={
                      <Message
                        id="buttonsV3.logoExperience.wr.modal.checkbox"
                        link={(innerMessage) => (
                          <Link
                            className="business-profile-url"
                            href="/businessprofile/settings/info/"
                          >
                            {innerMessage}
                          </Link>
                        )}
                      />
                    }
                    helperText={new MessageFormat(worldReady, {
                      id: 'buttonsV3.logoExperience.modal.checkboxHelpText',
                    }).format()}
                  />
                </div>
              </label>
            )}
            {dropzoneError && (
              <Alert type="warning" className="dropzone-error">
                {dropzoneError}
              </Alert>
            )}
            <Button
              onClick={saveLogo}
              className="save-button"
              btnState={showSpinner ? 'processing' : 'initial'}
            >
              {saveButton}
            </Button>
          </>
        ) : (
          <>
            {dropzoneError && (
              <Alert type="warning" className="dropzone-error">
                {dropzoneError}
              </Alert>
            )}
            {showSaveButton && (
              <Button
                onClick={saveLogo}
                className="save-button"
                btnState={showSpinner ? 'processing' : 'initial'}
              >
                {saveButton}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

DropzoneLogo.propTypes = {
  dispatch: PropTypes.func,
  uploadImage: PropTypes.func,
  updateBusinessProfileLogo: PropTypes.func,
  updateBusinessImageUrls: PropTypes.func,
  closeModal: PropTypes.func,
  logoUrl: PropTypes.string,
  setLogoImg: PropTypes.func,
  logoTarget: PropTypes.string,
  isLogoBusinessProfile: PropTypes.bool,
  setIsLogoBusinessProfile: PropTypes.func,
};

export default DropzoneLogo;
